import { FC, useContext, useEffect, useState } from 'react';
import { Document } from '@contentful/rich-text-types';
import clsx from 'clsx';
import router from 'next/router';
import DateTimeRenderer from '../DateTime/dateTime';
import {
  ChallengeButtonTypes,
  IChallengeProps,
  TChallengeModalSubType,
} from '../../models/Challenge/@types';
import { ChallengeTabs, getChallengeBaseUrl } from '../../models/Challenge';
import { OVERVIEW_TAB, ROUTES } from '../../Constants/variables';
import helpers, { DATE_TIME_FORMAT, getTabLink } from '../../utils/helpers';
import { useIsMobileViewport, useIsTabletViewport } from '../../hooks/useIsMobileViewport';

import AuthContext from '../../AppContext/AuthProvider';
import Button from '../Button';
import { ChallengeNSkillBuilderStatusForUser } from '../../models/User';
import CountDownTimer from '../CountDownTimer';
import ImageComponent from '../Image';
import InformativeModal from '../../blades/informativeModal/informativeModal';
import ModalWrapper from '../ModalWrapper';
import RichText from '../RichText';
import SectionWrapper from '../SectionWrapper';
import SvgLib from '../SvgLib';
import { TRole } from '../../models/auth';
import Typo from '../Typo';
import VideoPlayer from '../VideoPlayer';
import styles from './challengeHero.module.sass';
import useChallengeHeroActions from './useChallengeHeroActions';

export interface ChallengeButtonProps {
  challengeStatus: ChallengeNSkillBuilderStatusForUser;
  projectUrl: string;
  setIsModalActive: (value?: boolean) => void;
  role: string;
  challengeButtonText: ChallengeButtonTypes;
}

const ChallengeHero: FC<IChallengeProps> = props => {
  const { fetchingUser, appUser, setCurrentChallengeId } = useContext(AuthContext);
  const { isMobile } = useIsMobileViewport();
  const {
    title,
    description,
    media,
    image,
    videoUrl,
    industryList,
    skills,
    earnPoints,
    award,
    dueDate,
    partners,
    slug,
    entryId,
    launchDate,
    currentTab,
  } = props;
  const { modalData, challengeStatus, projectIdForChallenge } = useChallengeHeroActions(
    entryId ?? slug ?? '',
  );
  const [isModalActive, setIsModalActive] = useState<boolean>(false);
  const industry = industryList?.fields?.title?.join(', ');
  const baseUrl = getChallengeBaseUrl(props);

  const { isTablet } = useIsTabletViewport();

  const [showChallengeState, setShowChallengeState] = useState(false);

  const projectUrl = projectIdForChallenge
    ? helpers.generatePath(ROUTES.PROJECT, {
        projectId: projectIdForChallenge,
      })
    : projectIdForChallenge;

  let challengeButtonText: ChallengeButtonTypes = 'Loading Challenge Detail';
  if (fetchingUser) {
    challengeButtonText = 'Loading Challenge Detail';
  } else if (!fetchingUser && !appUser) {
    challengeButtonText = 'Join Challenge';
  } else if (!appUser) {
    challengeButtonText = 'Join Challenge';
  }

  const isNotLaunched = new Date(launchDate) >= new Date();

  const scrollToTabs = () => {
    if (document !== null) {
      const challengeHeroDivId = document.getElementById('challengeHeroDivId');

      window.scrollTo({
        top: challengeHeroDivId?.offsetHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    setCurrentChallengeId(entryId ?? slug);
    const pathName = router.query;
    pathName.slug &&
      !pathName.slug?.includes(OVERVIEW_TAB) &&
      pathName.slug?.length < 3 &&
      scrollToTabs();
    const handleScroll = () => {
      const yOffSet = window.pageYOffset;
      setShowChallengeState(yOffSet > 850);
    };
    window.addEventListener('scroll', handleScroll);
    return function cleanupListener() {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [entryId, setCurrentChallengeId, slug]);

  useEffect(() => {
    if (localStorage.getItem('open_modal') === 'true') {
      setIsModalActive(true);
      localStorage.removeItem('open_modal');
    }
  }, [setIsModalActive]);
  switch (challengeStatus) {
    case 'active':
      challengeButtonText = 'Active: Go To Project';
      break;
    case 'completed':
      if (new Date(dueDate) < new Date()) challengeButtonText = 'Completed';
      else challengeButtonText = 'Project Submitted';
      break;
    case 'no_participation':
      challengeButtonText = 'Join Challenge';
      break;
    default:
      break;
  }

  const createProjectModal = modalData.find(
    m => m?.fields?.subType === TChallengeModalSubType.CREATE_PROJECT,
  );
  const createAccountModal = modalData.find(
    m => m?.fields?.subType === TChallengeModalSubType.CREATE_ACCOUNT,
  );
  const modal = appUser ? createProjectModal : createAccountModal;
  return (
    <div id="challengeHeroDivId">
      {/* TODO: Add auth based modal */}
      {modal && (
        <ModalWrapper
          isActive={isModalActive}
          onClose={() => setIsModalActive(false)}
          hasCloseButton
        >
          <InformativeModal {...modal} />
        </ModalWrapper>
      )}
      <SectionWrapper
        classes={`${styles.challengeScroll} py-4 ${showChallengeState ? '' : 'is-invisible'} `}
        childClasses={`is-flex ${isMobile ? 'is-flex-direction-column	' : ''} ${
          launchDate && new Date(launchDate) <= new Date()
            ? 'is-justify-content-space-between	'
            : 'is-justify-content-flex-end'
        }`}
      >
        {isMobile && (
          <Typo h3 classes="mb-4">
            {title}
          </Typo>
        )}
        {!isMobile && launchDate && new Date(launchDate) <= new Date() && (
          <div className={`${styles.tabs} is-flex is-7`}>
            {ChallengeTabs.map(tab => (
              <Button
                ariaLabel={tab.slug}
                key={tab.slug}
                onlyText
                eventLink={!isMobile}
                underline
                link={getTabLink(tab, baseUrl, tab.widgetKey)}
                // classes={`${tab.slug === currentTab?.slug ? "selected" : ""}`}
                classes={clsx(styles.tabItem, {
                  selected: tab.slug === currentTab?.slug,
                })}
                onClick={scrollToTabs}
              />
            ))}
          </div>
        )}
        <div
          className={`is-flex  ${
            isMobile ? 'is-flex-direction-column	' : 'is-flex-direction-row'
          } ${styles.challengeBtn}`}
        >
          {new Date(dueDate) > new Date() ? (
            <DateTimeRenderer
              date={dueDate}
              dateFormat={DATE_TIME_FORMAT}
              textBefore="Due: "
              typoClasses="has-text-centered"
              typoProps={{ small: true }}
            />
          ) : (
            <Typo small classes="has-text-centered">
              Earn Points!
            </Typo>
          )}
          <Button
            icon="tickMark"
            iconClass="ml-3"
            classes={`${challengeButtonText === 'Loading Challenge Detail' && 'is-loading'}`}
            onClick={() => {
              // storing path in local storage for redirection after login
              if (!appUser) {
                localStorage.setItem('challengeId', entryId);
                localStorage.setItem('last_url', router.asPath);
              }
              challengeStatus === 'active'
                ? router.push(projectUrl)
                : challengeButtonText !== 'Loading Challenge Detail' && setIsModalActive(true);
            }}
            disabled={
              challengeStatus === 'completed' || appUser?.role === TRole.MENTOR || isNotLaunched
            }
          >
            {challengeButtonText}
          </Button>
        </div>
      </SectionWrapper>
      <SectionWrapper classes=" is-color-White is-bgcolor-Blue">
        <div className={`${styles.root} ${isTablet ? 'has-text-centered	' : ''}`}>
          <div>
            <Typo h1 classes="mb-5">
              {title}
            </Typo>
            <div>
              <Button
                theme="Filled - Lime"
                classes={`${
                  challengeButtonText === 'Loading Challenge Detail' && 'is-loading'
                } mb-4`}
                onClick={() => {
                  // storing path in local storage for redirection after login
                  if (!appUser) {
                    localStorage.setItem('challengeId', entryId);
                    localStorage.setItem('last_url', router.asPath);
                  }
                  challengeStatus === 'active'
                    ? router.push(projectUrl)
                    : challengeButtonText !== 'Loading Challenge Detail' && setIsModalActive(true);
                }}
                disabled={
                  challengeStatus === 'completed' || appUser?.role === TRole.MENTOR || isNotLaunched
                }
              >
                {challengeButtonText}
              </Button>
              {/* <Button
                theme="Filled - Lime"
                classes="mb-6"
                onClick={() => eventEmitor(Events.CREATE_PROJECT)}
                disabled={isChallengeJoined}
              >
                Join Challenge
              </Button> */}
            </div>
            <div
              className={`is-flex mb-5 ${
                isTablet && 'is-justify-content-center is-flex-wrap-wrap'
              }`}
            >
              <div
                className={`is-flex is-flex-direction-column ${
                  isTablet ? 'mr-4 has-text-left' : 'mr-6'
                }`}
              >
                <Typo normal>Earns</Typo>
                <div className="is-flex is-align-items-center">
                  <SvgLib icon="earned" iconSize="Medium" color="white" />
                  <Typo h2>{`+${earnPoints} Points`}</Typo>
                </div>
              </div>
              {award && (
                <div
                  className={`is-flex is-flex-direction-column ${isTablet ? 'has-text-left' : ''}`}
                >
                  {new Date(dueDate) > new Date() && (
                    <>
                      <Typo normal>Winner</Typo>
                      <div className="is-flex is-align-items-center ">
                        <SvgLib icon="winner" iconSize="Medium" color="white" />
                        <Typo h3 classes="ml-2">
                          {award}
                        </Typo>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <Typo medium>
              <RichText document={description as unknown as Document} />
            </Typo>
          </div>
          <div>
            <div className="is-flex is-flex-direction-row is-justify-content-space-between mb-3">
              <Typo small>{industry}</Typo>
              {new Date(dueDate) > new Date() ? (
                <DateTimeRenderer
                  date={dueDate}
                  dateFormat={DATE_TIME_FORMAT}
                  textBefore="Due "
                  typoProps={{ small: true }}
                />
              ) : (
                <Typo small>Earn Points!</Typo>
              )}
            </div>
            <div
              className={`${styles.challengeDetails} ${
                media === 'Video' ? styles.challengeDetailsVideo : ''
              }`}
            >
              {media === 'Video' && videoUrl ? (
                <VideoPlayer videoUrl={videoUrl} />
              ) : (
                <ImageComponent image={image} />
              )}
              <div
                className={`${styles.challengePartners} is-flex is-justify-content-space-evenly`}
              >
                {partners &&
                  partners.map((partner, index) => {
                    return (
                      index < 3 && (
                        <ImageComponent
                          key={partner.sys?.contentType?.sys?.id}
                          image={partner.fields.image}
                          width={isMobile ? '55px' : '100px'}
                          height={isMobile ? '20px' : '40px'}
                        />
                      )
                    );
                  })}
              </div>
              {new Date(dueDate) > new Date() && (
                <>
                  <CountDownTimer
                    {...{ dueDate }}
                    classes={`${styles.timerPos} ${media === 'Video' ? styles.timerPosVideo : ''}`}
                  />
                  <div
                    className={`${styles.timeRemaining}  ${
                      media === 'Video' ? styles.timeRemainingVideo : ''
                    }`}
                  />
                </>
              )}
            </div>

            <div className={`${styles.skillDetails} mt-4`}>
              <Typo h4 classes="mb-3">{`Skills you'll develop`}</Typo>
              <ul>
                {skills?.fields?.title &&
                  skills?.fields?.title.map((skill, index) => {
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index}>
                        <Typo medium>{skill}</Typo>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};

export default ChallengeHero;
